import { useQuery } from '@tanstack/react-query';
import {
  getHomePageData,
  type LeaderboardCollection,
  type TrendingCollection,
  type FloorPriceGainerCollection,
  type PromotedNft,
} from '../api/analytics';

export interface HomePageData {
  featuredCollection: any | null;
  topVolume: LeaderboardCollection[];
  trending: TrendingCollection[];
  topGainers: FloorPriceGainerCollection[];
  topLosers: FloorPriceGainerCollection[];
  activePromotions: PromotedNft[];
}

/**
 * Hook to fetch all home page data in a single API call
 * Uses TanStack Query for caching and state management
 *
 * This replaces the previous useQueries approach that made 4+ separate calls
 * Now makes a single call to /api/v1/home which returns all data at once
 */
export function useHomePageData() {
  const { data: apiData, isPending, isError, error, refetch } = useQuery({
    queryKey: ['homePage'],
    queryFn: getHomePageData,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const data: HomePageData = {
    featuredCollection: apiData?.featured || null,
    topVolume: apiData?.topVolume || [],
    trending: apiData?.trending || [],
    topGainers: apiData?.topGainers || [],
    topLosers: apiData?.topLosers || [],
    activePromotions: apiData?.activePromotions || [],
  };

  return {
    data,
    isLoading: isPending,
    isError,
    errors: error ? [error] : [],
    refetch,
  };
}
